/**
 * 계층 표 — 서버가 보낸 ```tree 블록을 엘보와 세로 레일로 그린다.
 *
 * 서버는 행마다 조상 자취(t)만 싣고 선을 글자로 그리지 않는다. 글리프로 계단을 그리면
 * 폰트에 따라 자리마다 폭이 어긋난다 — 브라우저 기본 등폭 폰트가 Box Drawing 만 전각으로
 * 가진 경우가 있어 세로선이 든 자리만 넓어진다. 들여쓰기를 px 로 정하면 그 의존이 사라진다.
 *
 * 선은 CSS border 로 긋는다. SVG 는 라벨이 줄바꿈되며 변하는 행 높이를 따라오지 못해
 * 선이 끊긴다. align-items:stretch 인 flex 자식의 border-left 는 행 높이를 자동으로 따라간다.
 *
 * 열 폭·생략·셀 스타일은 마크다운 표와 같은 기계를 쓴다(fitColumns.js) —
 * 한 말풍선 안에 두 표가 나란히 서므로 접는 규칙도 생김새도 같아야 한다.
 *
 * 중립 leaf — pages/** 를 import 하지 않는다(aiMarkdown.js 와 같은 규약).
 */
import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Box } from "@mui/material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";

import { C } from "@plannel/components/ai/aiTokens";
import {
  CELL_PAD, CELL_SX, NoteLine, OmittedColsNote,
  columnCaps, headSx, measureText, useCellFont, useColumnFit,
} from "@plannel/components/ai/fitColumns";

// 블록 스키마 버전. 진화 규칙은 서버(tree_table.py)와 같다 —
//   필드를 더하는 변경은 이 값을 올리지 않고, 기존 필드의 뜻이 바뀔 때만 올린다.
const SCHEMA_VERSION = 1;
const CHEVRON = 14;          // 접기 손잡이 자리. 자식이 없는 행도 같은 폭을 비워 라벨을 맞춘다
const LINE_HEIGHT = 1.6;     // 말풍선과 같은 값을 셀에 못박는다 — 엘보 높이가 이것에 기댄다
// 엘보가 놓이는 높이 — 첫 줄의 한가운데다.
//   em 이라 Big/Small 폰트 토글을 따라간다. 고정 px 로 두면 글자만 커지고 엘보가 남는다.
//   라벨이 두 줄로 접혀도 첫 줄에 머문다 — 세로 가운데로 두면 줄 수에 따라 흔들린다.
const ELBOW_Y = `${LINE_HEIGHT / 2}em`;
// 연결선. 셀 격자(C.border)보다 진해야 한다 — 같은 색이면 선이 격자에 묻혀 구조가 안 읽힌다.
//   손잡이와 같은 톤이라 계층을 가리키는 것들이 한 무리로 보인다.
const LINE = `1px solid ${C.text3}`;

/** 자리 간격. 깊어질수록 좁히되 한 표 안에서는 전 행이 같은 값을 쓴다. */
export function stepOf(rows) {
  const maxD = rows.reduce((m, r) => Math.max(m, (r.t || []).length), 0);
  return maxD <= 6 ? 14 : maxD <= 10 ? 11 : 9;
}

/** 다음 행이 더 깊으면 자식이 있다 — 블록이 깊이 우선이라 이 비교로 충분하다. */
export const hasKid = (rows, i) =>
  i + 1 < rows.length && (rows[i + 1].t || []).length > (rows[i].t || []).length;

/**
 * 접힌 노드의 후손을 건너뛴 보이는 행 목록.
 *
 * 후손이 부모 바로 뒤에 이어져 있다는 블록의 계약에 기대어 한 번만 훑는다.
 */
export function visibleRows(rows, collapsed) {
  const out = [];
  let skip = -1;
  for (let i = 0; i < rows.length; i += 1) {
    const d = (rows[i].t || []).length;
    if (skip >= 0) {
      if (d > skip) continue;
      skip = -1;
    }
    out.push(i);
    if (collapsed.has(i)) skip = d;
  }
  return out;
}

export default function TreeTable({ spec }) {
  const { t } = useTranslation();
  const { lang, fontEpoch, bodyPx, bodyFont, boldFont } = useCellFont();
  const [collapsed, setCollapsed] = useState(() => new Set());

  const cols = spec.cols || [];
  const rows = spec.rows || [];
  const step = stepOf(rows);

  // 자연폭은 접힘과 무관하게 전 행으로 잰다.
  //   보이는 행만 재면 접기 -> 폭 변화 -> 말풍선 높이 변화 -> 대화 스크롤바 -> 다시 폭 변화로
  //   되먹임이 생긴다. 트리는 스트리밍 중 행이 늘지 않아 한 번 재면 끝난다.
  const natRef = useRef({ key: "", w: [] });
  const key = `${spec.id}\u0002${lang}\u0002${fontEpoch}|${bodyPx}|${step}`;
  if (natRef.current.key !== key) {
    const w = cols.map((c) => measureText(c.label, boldFont) + CELL_PAD);
    for (const r of rows) {
      const cells = r.c || [];
      const indent = (r.t || []).length * step + CHEVRON;
      const first = indent + measureText(cells[0] ?? "", bodyFont) + CELL_PAD;
      if (first > (w[0] || 0)) w[0] = first;
      for (let i = 1; i < cols.length; i += 1) {
        const cw = measureText(cells[i] ?? "", bodyFont) + CELL_PAD;
        if (cw > (w[i] || 0)) w[i] = cw;
      }
    }
    natRef.current = { key, w };
  }
  // 패킹이 ref 로 읽을 수 있게 열별 상한을 실어 둔다. 0 번이 계층 열이라 상한이 따로다.
  natRef.current.caps = columnCaps(cols.map((c) => !!c.num), 0);

  const { scrollRef, colRefs, widthsRef, visK } = useColumnFit(natRef, cols.length);

  if (!cols.length || !rows.length) return null;

  const k = Math.min(visK, cols.length);
  const visCols = cols.slice(0, k);
  const omittedCount = cols.length - k;
  const shown = visibleRows(rows, collapsed);
  // 얼마나 담겼는지는 서버가 수치로 실어 보낸다 — 모델에게 말하게 하면 확률적으로 샌다.
  //   전부 담은 표에는 수치가 없으므로 이 줄도 나오지 않는다.
  const cut = spec.m || null;
  const cutText = [
    cut && cut.total ? t("agentChatTreeRowsOmitted", { shown: cut.shown, total: cut.total }) : "",
    cut && cut.full ? t("agentChatTreeDepthOmitted", { shown: cut.depth, total: cut.full }) : "",
  ].filter(Boolean).join(" · ");
  const cellSx = { ...CELL_SX, lineHeight: LINE_HEIGHT };
  const headCellSx = { ...headSx(0), lineHeight: LINE_HEIGHT };

  const toggle = (i) => setCollapsed((prev) => {
    const next = new Set(prev);
    if (next.has(i)) next.delete(i); else next.add(i);
    return next;
  });

  const slotSx = { width: `${step}px`, flex: "none" };
  // 손잡이는 엘보의 가로 획과 같은 높이에 앉는다 — 눈높이가 어긋나면 계단이 흔들려 보인다.
  //   아이콘을 그 자리에 놓고 제 높이의 절반만큼 끌어올리므로 아이콘 크기가 바뀌어도 맞는다.
  const handleSlotSx = { width: `${CHEVRON}px`, flex: "none", position: "relative",
                         color: C.text3 };
  const handleIconSx = { position: "absolute", left: "50%", top: ELBOW_Y,
                         transform: "translate(-50%, -50%)", fontSize: 12 };
  const railSx = (on) => ({ ...slotSx, borderLeft: on ? LINE : "none" });
  // 자기 자리 — 세로 획은 아래로 형제가 있으면 끝까지, 마지막 자식이면 엘보에서 끊는다.
  const elbowSx = (more) => ({
    ...slotSx, position: "relative",
    "&::before": {
      content: '""', position: "absolute", left: 0, top: 0,
      height: more ? "100%" : ELBOW_Y, borderLeft: LINE,
    },
    "&::after": {
      content: '""', position: "absolute", left: 0, top: ELBOW_Y,
      width: `${step}px`, borderTop: LINE,
    },
  });

  return (
    <Box sx={{ width: "100%", my: 0.8 }}>
      <Box ref={scrollRef} sx={{ overflowX: "hidden" }}>
        <Box component="table"
          sx={{ tableLayout: "fixed", width: "100%", borderCollapse: "separate", borderSpacing: 0 }}>
          <colgroup>
            {visCols.map((c, i) => (
              <col key={i}
                ref={(el) => { colRefs.current[i] = el; }}
                style={{ width: widthsRef.current[i] ? `${widthsRef.current[i]}px` : undefined }} />
            ))}
          </colgroup>
          <thead>
            <tr>
              {visCols.map((c, i) => (
                <Box component="th" key={i}
                  sx={{ ...headCellSx, textAlign: c.num ? "right" : "left" }}>
                  {c.label}
                </Box>
              ))}
            </tr>
          </thead>
          <tbody>
            {shown.map((ri) => {
              const r = rows[ri];
              const trail = r.t || [];
              const cells = r.c || [];
              const kid = hasKid(rows, ri);
              const open = !collapsed.has(ri);
              // 손잡이 자리 — 접을 수 있으면 셰브론, 자손이 절단으로 빠졌으면 그 사실만 알리고,
              //   진짜 잎이면 비운다. 셋 다 폭이 같아 라벨의 시작 위치가 흔들리지 않는다.
              const handle = kid ? (
                <Box component="span" role="button" tabIndex={0}
                  onClick={() => toggle(ri)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") { e.preventDefault(); toggle(ri); }
                  }}
                  aria-label={cells[0] || ""} aria-expanded={open}
                  sx={{ ...handleSlotSx, cursor: "pointer" }}>
                  {open
                    ? <KeyboardArrowDownIcon sx={handleIconSx} />
                    : <KeyboardArrowRightIcon sx={handleIconSx} />}
                </Box>
              ) : r.x ? (
                <Box component="span" aria-hidden="true"
                  sx={{ ...handleSlotSx, opacity: 0.6 }}>
                  <MoreHorizIcon sx={handleIconSx} />
                </Box>
              ) : <Box component="span" sx={{ width: `${CHEVRON}px`, flex: "none" }} />;
              return (
                <tr key={ri}>
                  <Box component="td" sx={cellSx}>
                    <Box sx={{ display: "flex", alignItems: "stretch", minWidth: 0 }}>
                      {trail.slice(0, -1).map((on, j) => <Box key={j} sx={railSx(!!on)} />)}
                      {trail.length > 0 && <Box sx={elbowSx(!!trail[trail.length - 1])} />}
                      {handle}
                      <Box component="span"
                        sx={{ flex: 1, minWidth: 0, wordBreak: "break-word",
                              overflowWrap: "anywhere" }}>
                        {cells[0] ?? ""}
                      </Box>
                    </Box>
                  </Box>
                  {visCols.slice(1).map((c, j) => (
                    <Box component="td" key={j}
                      sx={{ ...cellSx, textAlign: c.num ? "right" : "left" }}>
                      {cells[j + 1] ?? ""}
                    </Box>
                  ))}
                </tr>
              );
            })}
          </tbody>
        </Box>
      </Box>
      <NoteLine text={cutText} tip={t("agentChatTreeTruncatedTip")} />
      <OmittedColsNote n={omittedCount} />
    </Box>
  );
}

/**
 * 펜스 본문(JSON)을 받아 그린다.
 *
 * 모르는 상위 버전도 그린다. 필드를 더하는 변경은 버전을 올리지 않기로 했으므로
 * 올라간 버전은 이 구조 위에 무언가 더한 것이고, 모르는 키는 안 읽으면 그만이다.
 * 거절하면 본문은 표를 약속했는데 화면만 비는 조합이 배포 시차마다 재현된다 —
 * 서버와 정적 파일이 따로 나가고 브라우저가 옛 번들을 들고 있는 동안이다.
 * 아래 버전이나 버전이 없는 블록은 구조를 장담할 수 없어 그리지 않는다.
 *
 * 표가 통째로 사라지는 실패라 조용히 넘기지 않고 콘솔에 남긴다.
 * 차트 펜스에는 버전이 없어 파싱 실패만 보므로, 그쪽과 같은 판단은 아니다.
 */
export function TreeBlock({ raw }) {
  let spec = null;
  try {
    spec = JSON.parse(raw);
  } catch (e) {
    console.warn("[TreeBlock] 계층 블록을 읽지 못했다", e);
    return null;
  }
  if (!spec || !(spec.rows || []).length) {
    console.warn("[TreeBlock] 그릴 행이 없는 계층 블록");
    return null;
  }
  if (!(spec.v >= SCHEMA_VERSION)) {
    console.warn("[TreeBlock] 읽을 수 없는 스키마 버전", spec.v);
    return null;
  }
  if (spec.v > SCHEMA_VERSION) {
    console.warn("[TreeBlock] 새 스키마 버전을 아는 만큼만 그린다", spec.v);
  }
  return <TreeTable spec={spec} />;
}
